import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Package,
  PlusCircle,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  MoreVertical,
  Loader2,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/data-table";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { PageContainer } from "@/components/layout/PageContainer";
import DefaultEditor from "react-simple-wysiwyg";

const SpecialParts = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  // Reset pagination when search text changes
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText]);

  const [formData, setFormData] = useState({
    id: null,
    repair_job_id: "",
    item_name: "",
    brand_id: "",
    model_id: "",
    required_quantity: 1,
    unit_cost: 0,
    retail_price: null,
    supplier_id: "",
    order_link: "",
    tracking_id: "",
    order_notes: "",
    order_date: "",
    is_tax_exclusive: false,
    has_purchase_order: false,
    purchase_order_id: "",
    status: "pending",
  });

  const [statusData, setStatusData] = useState({
    status: "",
    notes: "",
  });

  const [receiveData, setReceiveData] = useState({
    received_date: "",
    notes: "",
  });

  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [newModelName, setNewModelName] = useState("");
  const [selectedBrandForModel, setSelectedBrandForModel] = useState("");


  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    return permissions && permissions.includes(perm);
  };
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [repairJobSearchInput, setRepairJobSearchInput] = useState("");
  const [repairJobSearchTerm, setRepairJobSearchTerm] = useState("");

  const [brandLabels, setBrandLabels] = useState({});
  const [modelLabels, setModelLabels] = useState({});
  const [supplierLabels, setSupplierLabels] = useState({});
  const [repairJobLabels, setRepairJobLabels] = useState({});

  React.useEffect(() => {
    const timer = setTimeout(() => setBrandSearchTerm(brandSearchInput), 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  React.useEffect(() => {
    const timer = setTimeout(() => setModelSearchTerm(modelSearchInput), 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);

  React.useEffect(() => {
    const timer = setTimeout(
      () => setSupplierSearchTerm(supplierSearchInput),
      300,
    );
    return () => clearTimeout(timer);
  }, [supplierSearchInput]);

  React.useEffect(() => {
    const timer = setTimeout(
      () => setRepairJobSearchTerm(repairJobSearchInput),
      300,
    );
    return () => clearTimeout(timer);
  }, [repairJobSearchInput]);

  // ==================== QUERIES ====================
  const { data: partData, isLoading: partsLoading } = useQuery({
    queryKey: [
      "special-parts",
      pagination.pageIndex,
      pagination.pageSize,
      searchText,
    ],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.REPAIRS.SPECIAL_PARTS, {
        params: {
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          search: searchText,
        },
      });
      return response.data;
    },
  });

  React.useEffect(() => {
    if (partData?.data) {
      const bLabels = {};
      const mLabels = {};
      const sLabels = {};
      const rLabels = {};
      partData.data.forEach((item) => {
        if (item.brand) bLabels[String(item.brand_id)] = item.brand.name;
        if (item.model) mLabels[String(item.model_id)] = item.model.name;
        if (item.supplier)
          sLabels[String(item.supplier_id)] = item.supplier.name;
        if (item.repair_job)
          rLabels[String(item.repair_job_id)] = item.repair_job.repair_number;
      });
      setBrandLabels((prev) => ({ ...prev, ...bLabels }));
      setModelLabels((prev) => ({ ...prev, ...mLabels }));
      setSupplierLabels((prev) => ({ ...prev, ...sLabels }));
      setRepairJobLabels((prev) => ({ ...prev, ...rLabels }));
    }
  }, [partData]);

  const specialParts = partData?.data || [];
  const pageCount = partData?.meta?.last_page || 1;
  const totalCount = partData?.meta?.total || 0;

  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: { search: brandSearchTerm || undefined },
      });
      return response.data.data || response.data || [];
    },
    enabled: hasPermission("brand read"),
  });

  const { data: models = [], isFetching: isModelsFetching } = useQuery({
    queryKey: ["models", formData.brand_id, modelSearchTerm],
    queryFn: async () => {
      if (!formData.brand_id) return [];
      const response = await apiClient.get(
        `${API_BASE_URL}/brand/${formData.brand_id}/model`,
        {
          params: { search: modelSearchTerm || undefined },
        },
      );
      return response.data.data || response.data || [];
    },
    enabled: !!formData.brand_id && hasPermission("brand model read"),
  });

  const { data: suppliers = [], isFetching: isSuppliersFetching } = useQuery({
    queryKey: ["suppliers", supplierSearchTerm],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.PURCHASES.SUPPLIERS, {
        params: { search: supplierSearchTerm || undefined },
      });
      return response.data.data || response.data || [];
    },
  });

  const { data: repairJobs = [], isFetching: isRepairJobsFetching } = useQuery({
    queryKey: ["repair-jobs", repairJobSearchTerm],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.REPAIRS.JOBS, {
        params: { search: repairJobSearchTerm || undefined },
      });
      return response.data.data || response.data || [];
    },
  });

  React.useEffect(() => {
    if (brands.length > 0) {
      setBrandLabels((prev) => {
        const next = { ...prev };
        brands.forEach((b) => {
          next[String(b.id)] = b.name;
        });
        return next;
      });
    }
  }, [brands]);

  React.useEffect(() => {
    if (models.length > 0) {
      setModelLabels((prev) => {
        const next = { ...prev };
        models.forEach((m) => {
          next[String(m.id)] = m.name;
        });
        return next;
      });
    }
  }, [models]);

  React.useEffect(() => {
    if (suppliers.length > 0) {
      setSupplierLabels((prev) => {
        const next = { ...prev };
        suppliers.forEach((s) => {
          next[String(s.id)] = s.name;
        });
        return next;
      });
    }
  }, [suppliers]);

  React.useEffect(() => {
    if (repairJobs.length > 0) {
      setRepairJobLabels((prev) => {
        const next = { ...prev };
        repairJobs.forEach((j) => {
          next[String(j.id)] = j.repair_number;
        });
        return next;
      });
    }
  }, [repairJobs]);

  // ==================== MUTATIONS ====================
  const saveMutation = useMutation({
    mutationFn: async (submitData) => {
      const url = submitData.id
        ? `${API_ENDPOINTS.REPAIRS.SPECIAL_PARTS}/${submitData.id}`
        : API_ENDPOINTS.REPAIRS.SPECIAL_PARTS;
      const method = submitData.id ? "PUT" : "POST";
      const response = await apiClient({ method, url, data: submitData });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Special part saved successfully");
      queryClient.invalidateQueries({ queryKey: ["special-parts"] });
      setModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to save special part",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`${API_ENDPOINTS.REPAIRS.SPECIAL_PARTS}/${id}`);
    },
    onSuccess: () => {
      toast.success("Special part deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["special-parts"] });
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete special part",
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, notes }) => {
      const response = await apiClient.post(
        `${API_ENDPOINTS.REPAIRS.SPECIAL_PARTS}/${id}/status`,
        { status, notes },
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Status updated successfully");
      setStatusModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["special-parts"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const receiveMutation = useMutation({
    mutationFn: async ({ id, received_date, notes }) => {
      const response = await apiClient.post(
        `${API_ENDPOINTS.REPAIRS.SPECIAL_PARTS}/${id}/receive`,
        { received_date, notes },
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Part marked as received successfully");
      setReceiveModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["special-parts"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to mark as received",
      );
    },
  });

  const brandMutation = useMutation({
    mutationFn: async (name) => {
      const response = await apiClient.post(
        API_ENDPOINTS.INVENTORY.BRANDS,
        { name },
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Brand added successfully");
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      setBrandModalOpen(false);
      setNewBrandName("");
      if (data.data) {
        setBrandLabels((prev) => ({
          ...prev,
          [String(data.data.id)]: data.data.name,
        }));
        setFormData((prev) => ({
          ...prev,
          brand_id: data.data.id,
          model_id: "",
        }));
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add brand");
    },
  });

  const modelMutation = useMutation({
    mutationFn: async ({ name, brand_id }) => {
      const response = await apiClient.post(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(brand_id),
        { name, brand_id },
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Model added successfully");
      queryClient.invalidateQueries({ queryKey: ["models"] });
      setModelModalOpen(false);
      setNewModelName("");
      if (data.data) {
        setModelLabels((prev) => ({
          ...prev,
          [String(data.data.id)]: data.data.name,
        }));
        setFormData((prev) => ({
          ...prev,
          brand_id: data.data.brand_id || selectedBrandForModel,
          model_id: data.data.id,
        }));
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add model");
    },
  });

  // ==================== HELPERS ====================
  const resetForm = () => {
    setFormData({
      id: null,
      repair_job_id: "",
      item_name: "",
      brand_id: "",
      model_id: "",
      required_quantity: 1,
      unit_cost: 0,
      retail_price: null,
      supplier_id: "",
      order_link: "",
      tracking_id: "",
      order_notes: "",
      order_date: "",
      is_tax_exclusive: false,
      has_purchase_order: false,
      purchase_order_id: "",
      status: "pending",
    });
  };

  const handleAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleEdit = (part) => {
    setFormData(part);
    setModalOpen(true);
  };

  const handleSave = () => {
    if (
      !formData.item_name ||
      !formData.brand_id ||
      !formData.model_id ||
      !formData.supplier_id
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const submitData = {
      ...formData,
      required_quantity: parseInt(formData.required_quantity),
      unit_cost: parseFloat(formData.unit_cost),
      retail_price: formData.retail_price
        ? parseFloat(formData.retail_price)
        : null,
      brand_id: parseInt(formData.brand_id),
      model_id: parseInt(formData.model_id),
      repair_job_id: formData.repair_job_id || null,
      purchase_order_id: formData.purchase_order_id || null,
      order_date: formData.order_date || null,
    };

    saveMutation.mutate(submitData);
  };

  const handleStatusUpdate = (part) => {
    setSelectedPart(part);
    setStatusData({ status: part.status || "pending", notes: "" });
    setStatusModalOpen(true);
  };

  const handleMarkReceived = (part) => {
    setSelectedPart(part);
    setReceiveData({
      received_date: new Date().toISOString().slice(0, 16),
      notes: "",
    });
    setReceiveModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    const statusConfig = {
      pending: { variant: "warning" },
      ordered: { variant: "info" },
      shipped: { variant: "secondary" },
      received: { variant: "success" },
      cancelled: { variant: "danger" },
    };

    const config = statusConfig[s] || {
      variant: "secondary",
    };

    return (
      <Badge variant={config.variant}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const getBrandName = (brandId) => {
    if (brandLabels[String(brandId)]) return brandLabels[String(brandId)];
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : "Unknown";
  };

  const getModelName = (modelId) => {
    if (modelLabels[String(modelId)]) return modelLabels[String(modelId)];
    const model = models.find((m) => m.id === parseInt(modelId));
    return model ? model.name : "-";
  };

  const getSupplierName = (supplierId) => {
    if (supplierLabels[String(supplierId)])
      return supplierLabels[String(supplierId)];
    const supplier = suppliers.find((s) => s.id === supplierId);
    return supplier ? supplier.name : "Unknown";
  };

  const getFilteredModels = () => {
    return models;
  };

  // ==================== TABLE COLUMNS ====================
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "item_name",
        header: "Item Name",
      },
      {
        accessorFn: (row) => getBrandName(row.brand_id),
        header: "Brand",
      },
      {
        accessorFn: (row) => getModelName(row.model_id),
        header: "Model",
      },
      {
        accessorKey: "required_quantity",
        header: "Quantity",
      },
      {
        accessorKey: "unit_cost",
        header: "Unit Cost",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue() || 0).toFixed(2)}`,
      },
      {
        accessorKey: "retail_price",
        header: "Retail Price",
        cell: ({ getValue }) => {
          const value = getValue();
          return value
            ? `${currencySymbol}${parseFloat(value).toFixed(2)}`
            : "-";
        },
      },
      {
        accessorFn: (row) => getSupplierName(row.supplier_id),
        header: "Supplier",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => getStatusBadge(getValue()),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const part = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("special part update") && (
                  <DropdownMenuItem onClick={() => handleEdit(part)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {part.status !== "cancelled" &&
                  hasPermission("special part update_status") && (
                    <DropdownMenuItem onClick={() => handleStatusUpdate(part)}>
                      <RefreshCw className="mr-2 h-4 w-4" /> Update Status
                    </DropdownMenuItem>
                  )}
                {part.status !== "received" &&
                  hasPermission("special part receive") && (
                    <DropdownMenuItem onClick={() => handleMarkReceived(part)}>
                      <CheckCircle className="mr-2 h-4 w-4" /> Mark Received
                    </DropdownMenuItem>
                  )}
                <DropdownMenuSeparator />
                {hasPermission("special part delete") && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setDeleteConfirmationId(part.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brands, models, suppliers, currencySymbol, permissions],
  );

  if (!hasPermission("special part read")) {
    return (
      <PageContainer>
        <div className="flex h-[200px] items-center justify-center">
          <p className="text-muted-foreground">
            You do not have permission to view special parts.
          </p>
        </div>
      </PageContainer>
    );
  }

  if (partsLoading) {
    return (
      <div className="flex h-[100px] w-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Special Parts"
          subtitle="Manage special repair parts orders"
        />
        <PageHeaderActions>
          {hasPermission("special part create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Part
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search parts..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={specialParts}
          getRowId={(row) => row.id}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={partsLoading}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total parts: {totalCount}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {formData.id ? "Edit" : "Add New"} Special Part
            </DialogTitle>
            <DialogDescription>
              Fill in the details for the special part order
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={formData.item_name}
                onChange={(e) =>
                  setFormData({ ...formData, item_name: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Brand *</Label>
                {hasPermission("brand create") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBrandModalOpen(true)}
                    className="h-auto py-1 px-2 text-xs"
                  >
                    <PlusCircle className="mr-1 h-3 w-3" />
                    Add New
                  </Button>
                )}
              </div>
              <ReactSelect
                onInputChange={(value) => setBrandSearchInput(value)}
                options={brands.map((b) => ({ value: b.id, label: b.name }))}
                value={
                  formData.brand_id
                    ? {
                      value: formData.brand_id,
                      label:
                        brandLabels[String(formData.brand_id)] ||
                        brands.find((b) => b.id === formData.brand_id)
                          ?.name ||
                        "Select Brand",
                    }
                    : null
                }
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    brand_id: option?.value || "",
                    model_id: "",
                  })
                }
                placeholder="Search Brand..."
                isLoading={isBrandsFetching}
                isClearable
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Model *</Label>
                {hasPermission("brand model create") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!formData.brand_id) {
                        toast.error("Please select a brand first");
                        return;
                      }
                      setSelectedBrandForModel(formData.brand_id);
                      setModelModalOpen(true);
                    }}
                    className="h-auto py-1 px-2 text-xs"
                    disabled={!formData.brand_id}
                  >
                    <PlusCircle className="mr-1 h-3 w-3" />
                    Add New
                  </Button>
                )}
              </div>
              <ReactSelect
                onInputChange={(value) => setModelSearchInput(value)}
                options={getFilteredModels().map((m) => ({
                  value: m.id,
                  label: m.name,
                }))}
                value={
                  formData.model_id
                    ? {
                      value: formData.model_id,
                      label:
                        modelLabels[String(formData.model_id)] ||
                        getFilteredModels().find(
                          (m) =>
                            m.id === formData.model_id ||
                            m.id === parseInt(formData.model_id),
                        )?.name ||
                        "Select Model",
                    }
                    : null
                }
                onChange={(option) =>
                  setFormData({ ...formData, model_id: option?.value || "" })
                }
                placeholder={
                  formData.brand_id ? "Search Model..." : "Pick a brand first"
                }
                isDisabled={!formData.brand_id}
                isLoading={isModelsFetching}
                isClearable
              />
            </div>

            <div className="space-y-2">
              <Label>Supplier *</Label>
              <ReactSelect
                onInputChange={(value) => setSupplierSearchInput(value)}
                options={suppliers.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                value={
                  formData.supplier_id
                    ? {
                      value: formData.supplier_id,
                      label:
                        supplierLabels[String(formData.supplier_id)] ||
                        suppliers.find((s) => s.id === formData.supplier_id)
                          ?.name ||
                        "Select Supplier",
                    }
                    : null
                }
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    supplier_id: option?.value || "",
                  })
                }
                placeholder="Search Supplier..."
                isLoading={isSuppliersFetching}
                isClearable
              />
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={formData.required_quantity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    required_quantity: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Unit Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) =>
                  setFormData({ ...formData, unit_cost: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Retail Price</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.retail_price || ""}
                onChange={(e) =>
                  setFormData({ ...formData, retail_price: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Repair Job</Label>
              <ReactSelect
                onInputChange={(value) => setRepairJobSearchInput(value)}
                options={repairJobs.map((j) => ({
                  value: j.id,
                  label: j.repair_number,
                }))}
                value={
                  formData.repair_job_id
                    ? {
                      value: formData.repair_job_id,
                      label:
                        repairJobLabels[String(formData.repair_job_id)] ||
                        repairJobs.find(
                          (j) => j.id === formData.repair_job_id,
                        )?.repair_number ||
                        "Select Repair Job",
                    }
                    : null
                }
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    repair_job_id: option?.value || "",
                  })
                }
                placeholder="Search Repair Job..."
                isLoading={isRepairJobsFetching}
                isClearable
              />
            </div>

            <div className="space-y-2">
              <Label>Order Link</Label>
              <Input
                value={formData.order_link}
                onChange={(e) =>
                  setFormData({ ...formData, order_link: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Tracking ID</Label>
              <Input
                value={formData.tracking_id}
                onChange={(e) =>
                  setFormData({ ...formData, tracking_id: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input
                type="date"
                value={formData.order_date}
                onChange={(e) =>
                  setFormData({ ...formData, order_date: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Order Notes</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  value={formData.order_notes}
                  onChange={(e) =>
                    setFormData({ ...formData, order_notes: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="tax_exclusive"
                checked={formData.is_tax_exclusive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_tax_exclusive: checked })
                }
              />
              <Label htmlFor="tax_exclusive">Tax Exclusive</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save Part"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={statusData.status}
                onValueChange={(value) =>
                  setStatusData({ ...statusData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  value={statusData.notes}
                  onChange={(e) =>
                    setStatusData({ ...statusData, notes: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                statusMutation.mutate({
                  id: selectedPart.id,
                  status: statusData.status,
                  notes: statusData.notes,
                })
              }
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Modal */}
      <Dialog open={receiveModalOpen} onOpenChange={setReceiveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Received</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Received Date</Label>
              <Input
                type="datetime-local"
                value={receiveData.received_date}
                onChange={(e) =>
                  setReceiveData({
                    ...receiveData,
                    received_date: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  value={receiveData.notes}
                  onChange={(e) =>
                    setReceiveData({ ...receiveData, notes: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReceiveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                receiveMutation.mutate({
                  id: selectedPart.id,
                  received_date: receiveData.received_date,
                  notes: receiveData.notes,
                })
              }
            >
              Mark Received
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              special part.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteConfirmationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Brand Modal */}
      <Dialog open={brandModalOpen} onOpenChange={setBrandModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Brand</DialogTitle>
            <DialogDescription>
              Enter the name of the new brand
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Brand Name *</Label>
              <Input
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Enter brand name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newBrandName.trim()) {
                    brandMutation.mutate(newBrandName);
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBrandModalOpen(false);
                setNewBrandName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!newBrandName.trim()) {
                  toast.error("Brand name is required");
                  return;
                }
                brandMutation.mutate(newBrandName);
              }}
              disabled={brandMutation.isPending}
            >
              {brandMutation.isPending ? "Adding..." : "Add Brand"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Model Modal */}
      <Dialog open={modelModalOpen} onOpenChange={setModelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Model</DialogTitle>
            <DialogDescription>
              Enter the name of the new model for the selected brand
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                placeholder="Enter model name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newModelName.trim()) {
                    modelMutation.mutate({
                      name: newModelName,
                      brand_id: selectedBrandForModel,
                    });
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModelModalOpen(false);
                setNewModelName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!newModelName.trim()) {
                  toast.error("Model name is required");
                  return;
                }
                modelMutation.mutate({
                  name: newModelName,
                  brand_id: selectedBrandForModel,
                });
              }}
              disabled={modelMutation.isPending}
            >
              {modelMutation.isPending ? "Adding..." : "Add Model"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default SpecialParts;
